import { getCookieObject, setCookieObject } from "./cookies";
import { clearSessions } from "./client_identifier";
import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
} from "./localStorageTemporal";

const COOKIE_SESSION_IDENTIFIER_QUERY = "msm-identifier-landing-parameters";
const COOKIE_SESSION_DURATION_TIME = 1;

// List of query parameters that generates a new session because is a different incoming source
// ⚠️ IMPORTANT: keep this list in lower case ⚠️
const QUERY_PARAMS_CHECK = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "idCampaign",
  "gclid",
  "aid",
];

export type queryParametersToCheckType = {
  [K in (typeof QUERY_PARAMS_CHECK)[number]]?: string;
};

export type queryParametersToCheckRecordType = {
  [K: number]: queryParametersToCheckType;
};

/**
 * Automatically detects new parameters to be saved on a que
 * @returns The last landing query parameters to be tracked
 */
function getTrackingLandingParameters(
  locationSearch: string
): queryParametersToCheckType {
  // TODO add cache based on input + time

  const locationSearchLowerCase = locationSearch?.toLowerCase();
  const queryParameters = new URLSearchParams(locationSearchLowerCase);

  let oldQueryParameters = getCookieObject(
    COOKIE_SESSION_IDENTIFIER_QUERY
  ) as queryParametersToCheckType;
  if (oldQueryParameters === null) {
    oldQueryParameters = getLocalStorageWithExpiry(
      COOKIE_SESSION_IDENTIFIER_QUERY
    ) as queryParametersToCheckType;
  }

  if (oldQueryParameters === null) {
    // No previous query parameters saved, let's create a new ones:
    return saveLandingParameters(queryParameters);
  }

  if (
    isRequiredNewSessionBasedOnQueryParameters(
      queryParameters,
      oldQueryParameters
    )
  ) {
    // New incoming source
    clearSessions(); // Mark as new session
    return saveLandingParameters(queryParameters);
  }

  // The precious query parameters are valid
  return oldQueryParameters;
}

/**
 * Compare query parameters to check if is needed a new session for the flow.
 * @param queryParameters Current url parameters on the url, the news to check
 * @param oldQueryParameters previous url parameters saved to compare
 * @returns true if new session is required, false otherwise
 */
export function isRequiredNewSessionBasedOnQueryParameters(
  queryParameters: URLSearchParams,
  oldQueryParameters?: queryParametersToCheckType | null | undefined
): boolean {
  // There is no previous session, we need to create one, even without parameters
  if (
    !oldQueryParameters &&
    (!queryParameters || Array.from(queryParameters).length === 0)
  ) {
    return false;
  }

  // The current url does not have any query parameter useful to create a new session
  if (QUERY_PARAMS_CHECK.every((query) => !queryParameters.has(query))) {
    return false;
  }

  // Check all previous parameters are the same to the new ones.
  return QUERY_PARAMS_CHECK.some((query) => {
    const param = queryParameters.get(query);
    return oldQueryParameters?.hasOwnProperty(query)
      ? param === null || oldQueryParameters[query] !== param
      : param !== null;
  });
}

/**
 * Call this function when the landing parameters have change to save into a
 * @returns Landings parameters of the this current session
 */
function saveLandingParameters(
  queryParameters: URLSearchParams
): queryParametersToCheckType {
  let currentLandingParamsTracking;
  currentLandingParamsTracking = QUERY_PARAMS_CHECK.reduce(
    (obj, queryParamName) => {
      const value = queryParameters.get(queryParamName);
      if (value) {
        obj[queryParamName] = value;
      }
      return obj;
    },
    {}
  );

  if (Object.keys(currentLandingParamsTracking).length === 0) {
    currentLandingParamsTracking = undefined;
  }

  setCookieObject({
    name: COOKIE_SESSION_IDENTIFIER_QUERY,
    value: currentLandingParamsTracking,
    expireInDays: COOKIE_SESSION_DURATION_TIME,
  });
  setLocalStorageWithExpiry(
    COOKIE_SESSION_IDENTIFIER_QUERY,
    currentLandingParamsTracking,
    COOKIE_SESSION_DURATION_TIME * 1000 * 60 * 60 * 24
  );

  return currentLandingParamsTracking;
}

export { getTrackingLandingParameters };
